import { ThemeEvents } from '@theme/events';

const LOCK_ATTRIBUTE = 'data-hf-add-in-progress';
const QUEUED_ADD_ATTRIBUTE = 'data-hf-queued-add-pending';
const VARIANT_TRANSITION_ATTRIBUTE = 'data-hf-variant-change-in-progress';
const FALLBACK_UNLOCK_DELAY = 15000;
const VARIANT_TRANSITION_FALLBACK_DELAY = 15000;
const NETWORK_ERROR_DISPLAY_DURATION = 10000;
const NETWORK_ERROR_MESSAGE = 'We couldn’t add this item. Check your connection and try again.';
const variantTransitionTimeouts = new WeakMap();
const queuedAddTimeouts = new WeakMap();
const networkErrorTimeouts = new WeakMap();
const networkErrorTextNodes = new WeakMap();

function getEventScope(event) {
  return event.target instanceof Element
    ? event.target.closest('.shopify-section, dialog, product-card')
    : null;
}

function getScopedProductForms(event) {
  const scope = getEventScope(event);
  return scope ? Array.from(scope.querySelectorAll('product-form-component')) : [];
}

function getNetworkErrorTextNode(errorElement) {
  const existingTextNode = networkErrorTextNodes.get(errorElement);
  if (existingTextNode?.parentNode === errorElement) return existingTextNode;

  // Reuse the same third child text node that the native product form updates
  // for quantity and server-side cart errors. This prevents two messages from
  // being rendered at once after a network failure followed by another error.
  const nativeTextNode = errorElement.childNodes[2];
  if (nativeTextNode?.nodeType === Node.TEXT_NODE) {
    networkErrorTextNodes.set(errorElement, nativeTextNode);
    return nativeTextNode;
  }

  const textNode = document.createTextNode('');
  errorElement.appendChild(textNode);
  networkErrorTextNodes.set(errorElement, textNode);
  return textNode;
}

function showNetworkError(productForm) {
  const errorElement = productForm.refs?.addToCartTextError;
  const liveRegion = productForm.refs?.liveRegion;
  const errorTextNode = errorElement ? getNetworkErrorTextNode(errorElement) : null;

  if (errorElement && errorTextNode) {
    errorElement.classList.remove('hidden');
    errorTextNode.textContent = NETWORK_ERROR_MESSAGE;
  }

  if (liveRegion) {
    liveRegion.textContent = NETWORK_ERROR_MESSAGE;
  }

  const existingTimeout = networkErrorTimeouts.get(productForm);
  if (existingTimeout) clearTimeout(existingTimeout);

  const timeout = setTimeout(() => {
    if (errorTextNode?.textContent === NETWORK_ERROR_MESSAGE) {
      errorTextNode.textContent = '';
    }
    if (errorElement?.textContent?.trim() === '') {
      errorElement.classList.add('hidden');
    }
    if (liveRegion?.textContent === NETWORK_ERROR_MESSAGE) {
      liveRegion.textContent = '';
    }
    networkErrorTimeouts.delete(productForm);
  }, NETWORK_ERROR_DISPLAY_DURATION);

  networkErrorTimeouts.set(productForm, timeout);
}

function clearNetworkError(productForm) {
  const errorElement = productForm.refs?.addToCartTextError;
  const liveRegion = productForm.refs?.liveRegion;
  const errorTextNode = errorElement ? networkErrorTextNodes.get(errorElement) : null;

  if (errorTextNode?.textContent === NETWORK_ERROR_MESSAGE) {
    errorTextNode.textContent = '';
  }

  if (errorElement?.textContent?.trim() === '') {
    errorElement.classList.add('hidden');
  }

  if (liveRegion?.textContent === NETWORK_ERROR_MESSAGE) {
    liveRegion.textContent = '';
  }

  const timeout = networkErrorTimeouts.get(productForm);
  if (timeout) clearTimeout(timeout);
  networkErrorTimeouts.delete(productForm);
}

function normalizeVariantId(value) {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized || null;
}

function normalizePathname(pathname) {
  const normalized = pathname.replace(/\/+$/, '');
  return normalized || '/';
}

function isCartAddRequestUrl(requestUrl) {
  try {
    const request = new URL(requestUrl, window.location.origin);
    const cartAdd = new URL(
      window.Theme?.routes?.cart_add_url || '/cart/add.js',
      window.location.origin
    );

    return (
      request.origin === cartAdd.origin &&
      normalizePathname(request.pathname) === normalizePathname(cartAdd.pathname)
    );
  } catch {
    return false;
  }
}

function getRequestMethod(input, init) {
  const method = init?.method || (input instanceof Request ? input.method : 'GET');
  return String(method).toUpperCase();
}

function extractVariantIdsFromPayload(payload) {
  const variantIds = new Set();

  const addVariantId = (value) => {
    const variantId = normalizeVariantId(value);
    if (variantId) variantIds.add(variantId);
  };

  if (!payload) return variantIds;

  if (payload instanceof FormData || payload instanceof URLSearchParams) {
    for (const [key, value] of payload.entries()) {
      if (key === 'id' || key === 'items[][id]' || /items\[\d+\]\[id\]/.test(key)) {
        addVariantId(value);
      }
    }
    return variantIds;
  }

  if (typeof payload !== 'string') return variantIds;

  try {
    const parsedPayload = JSON.parse(payload);
    addVariantId(parsedPayload?.id);

    if (Array.isArray(parsedPayload?.items)) {
      for (const item of parsedPayload.items) {
        addVariantId(item?.id);
      }
    }
  } catch {
    const searchParams = new URLSearchParams(payload);
    addVariantId(searchParams.get('id'));
  }

  return variantIds;
}

function getRequestVariantIds(input, init) {
  const initVariantIds = extractVariantIdsFromPayload(init?.body);
  if (initVariantIds.size || !(input instanceof Request)) return initVariantIds;

  // Avoid consuming or cloning an existing Request body here. When the body is
  // unavailable synchronously, the guarded fallback releases every locked form.
  return new Set();
}

function getLockedProductForms(variantIds = new Set()) {
  const lockedForms = Array.from(
    document.querySelectorAll(
      `product-form-component[${LOCK_ATTRIBUTE}], product-form-component[${QUEUED_ADD_ATTRIBUTE}]`
    )
  );

  if (!variantIds.size) return lockedForms;

  const matchesVariantIds = (productForm) => {
    const variantInput = productForm.querySelector('input[name="id"]');
    return variantIds.has(normalizeVariantId(variantInput?.value));
  };

  const matchingLockedForms = lockedForms.filter(matchesVariantIds);
  if (matchingLockedForms.length) return matchingLockedForms;
  if (lockedForms.length) return lockedForms;

  // Queued submissions run after a variant transition without entering the
  // normal submission lock. Match their payload back to the visible form so a
  // rejected batch request still receives buyer-visible recovery feedback.
  return Array.from(document.querySelectorAll('product-form-component')).filter(matchesVariantIds);
}

function releaseLockedProductForms({ showFeedback = false, variantIds = new Set() } = {}) {
  for (const productForm of getLockedProductForms(variantIds)) {
    if (showFeedback) showNetworkError(productForm);
    productForm.dispatchEvent(new Event(ThemeEvents.cartError, { bubbles: true }));
  }
}

function instrumentCartAddJsonResponse(response, variantIds) {
  if (!response || typeof response.json !== 'function') return response;

  const nativeJson = response.json.bind(response);

  try {
    Object.defineProperty(response, 'json', {
      configurable: true,
      enumerable: false,
      writable: true,
      value: async () => {
        try {
          return await nativeJson();
        } catch (error) {
          // A response-body or JSON decoding failure occurs after fetch has
          // resolved, so release the same form lock and show retry feedback.
          releaseLockedProductForms({
            showFeedback: true,
            variantIds,
          });
          throw error;
        }
      },
    });
  } catch {
    // Preserve the native response when a browser exposes a non-extensible
    // Response object. The existing guarded timeout remains the fallback.
  }

  return response;
}

function armQueuedAddLock(productForm) {
  productForm.setAttribute(QUEUED_ADD_ATTRIBUTE, 'true');

  const existingTimeout = queuedAddTimeouts.get(productForm);
  if (existingTimeout) clearTimeout(existingTimeout);

  let fallbackTimeout;
  const unlock = () => {
    productForm.removeAttribute(QUEUED_ADD_ATTRIBUTE);
    clearTimeout(fallbackTimeout);
    productForm.removeEventListener(ThemeEvents.cartUpdate, unlock);
    productForm.removeEventListener(ThemeEvents.cartError, unlock);
    queuedAddTimeouts.delete(productForm);
  };

  productForm.addEventListener(ThemeEvents.cartUpdate, unlock);
  productForm.addEventListener(ThemeEvents.cartError, unlock);
  fallbackTimeout = setTimeout(unlock, FALLBACK_UNLOCK_DELAY);
  queuedAddTimeouts.set(productForm, fallbackTimeout);

  return unlock;
}

// The native product form catches rejected cart requests without emitting the
// theme's cart:error event. Mirror only failed cart-add requests so the
// submission lock releases immediately while preserving the original failure.
if (!window.hfCartNetworkReleaseInstalled) {
  const nativeFetch = window.fetch.bind(window);

  Object.defineProperty(window, 'hfCartNetworkReleaseInstalled', {
    value: true,
    configurable: false,
    enumerable: false,
    writable: false,
  });

  window.fetch = async function (input, init) {
    const requestUrl =
      typeof input === 'string'
        ? input
        : input instanceof Request
          ? input.url
          : String(input);
    const requestMethod = getRequestMethod(input, init);
    const isCartAddRequest =
      requestMethod === 'POST' && isCartAddRequestUrl(requestUrl);
    const requestVariantIds = isCartAddRequest ? getRequestVariantIds(input, init) : new Set();

    try {
      const response = await nativeFetch(input, init);
      return isCartAddRequest
        ? instrumentCartAddJsonResponse(response, requestVariantIds)
        : response;
    } catch (error) {
      if (isCartAddRequest) {
        releaseLockedProductForms({
          showFeedback: true,
          variantIds: requestVariantIds,
        });
      }
      throw error;
    }
  };
}

// Mirror the theme's private variant-transition state with a guarded DOM flag.
// This avoids relying only on URL timing while preserving the native queue.
document.addEventListener(
  ThemeEvents.variantSelected,
  (event) => {
    for (const productForm of getScopedProductForms(event)) {
      productForm.setAttribute(VARIANT_TRANSITION_ATTRIBUTE, 'true');

      const existingTimeout = variantTransitionTimeouts.get(productForm);
      if (existingTimeout) clearTimeout(existingTimeout);

      const timeout = setTimeout(() => {
        productForm.removeAttribute(VARIANT_TRANSITION_ATTRIBUTE);
        variantTransitionTimeouts.delete(productForm);
      }, VARIANT_TRANSITION_FALLBACK_DELAY);

      variantTransitionTimeouts.set(productForm, timeout);
    }
  },
  true
);

document.addEventListener(
  ThemeEvents.variantUpdate,
  (event) => {
    const updatedProductId = event.detail?.data?.newProduct?.id || event.detail?.data?.productId;

    for (const productForm of getScopedProductForms(event)) {
      if (updatedProductId && productForm.dataset.productId !== updatedProductId) continue;

      productForm.removeAttribute(VARIANT_TRANSITION_ATTRIBUTE);
      const timeout = variantTransitionTimeouts.get(productForm);
      if (timeout) clearTimeout(timeout);
      variantTransitionTimeouts.delete(productForm);
    }
  },
  true
);

/**
 * Prevents duplicate cart requests caused by rapid repeated submissions while
 * preserving the theme's existing variant-transition queue.
 */
const ProductForm = customElements.get('product-form-component');

if (ProductForm && !ProductForm.prototype.hfSubmissionGuardInstalled) {
  const originalHandleSubmit = ProductForm.prototype.handleSubmit;

  Object.defineProperty(ProductForm.prototype, 'hfSubmissionGuardInstalled', {
    value: true,
    configurable: false,
    enumerable: false,
    writable: false,
  });

  ProductForm.prototype.handleSubmit = function (event) {
    const form = this.querySelector('form');
    const variantInput = this.querySelector('input[name="id"]');
    const quantitySelector = this.refs?.quantitySelector;
    const urlVariant = new URL(window.location.href).searchParams.get('variant');
    const variantIsUpdating =
      this.hasAttribute(VARIANT_TRANSITION_ATTRIBUTE) ||
      (Boolean(urlVariant) && Boolean(variantInput?.value) && urlVariant !== variantInput.value);

    // The native queue accepts every submit during a variant transition. Hold
    // the first intent until its cart result so rapid repeat clicks cannot add
    // the same queued item multiple times.
    if (this.hasAttribute(QUEUED_ADD_ATTRIBUTE)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (variantIsUpdating) {
      clearNetworkError(this);
      const unlockQueuedAdd = armQueuedAddLock(this);

      try {
        return originalHandleSubmit.call(this, event);
      } catch (error) {
        unlockQueuedAdd();
        throw error;
      }
    }

    if (this.hasAttribute(LOCK_ATTRIBUTE)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (!form?.checkValidity()) {
      return originalHandleSubmit.call(this, event);
    }

    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      if (!validation.canAdd) {
        return originalHandleSubmit.call(this, event);
      }
    }

    const addButtons = this.querySelectorAll('add-to-cart-component [ref="addToCartButton"]');
    if (Array.from(addButtons).some((button) => button.disabled)) {
      return originalHandleSubmit.call(this, event);
    }

    clearNetworkError(this);
    this.setAttribute(LOCK_ATTRIBUTE, 'true');

    let fallbackTimeout;
    const unlock = () => {
      this.removeAttribute(LOCK_ATTRIBUTE);
      clearTimeout(fallbackTimeout);
      this.removeEventListener(ThemeEvents.cartUpdate, unlock);
      this.removeEventListener(ThemeEvents.cartError, unlock);
    };

    this.addEventListener(ThemeEvents.cartUpdate, unlock);
    this.addEventListener(ThemeEvents.cartError, unlock);
    fallbackTimeout = setTimeout(unlock, FALLBACK_UNLOCK_DELAY);

    try {
      return originalHandleSubmit.call(this, event);
    } catch (error) {
      unlock();
      throw error;
    }
  };
}
